import { AnalyticsEvent, AnalyticsProvider, PageViewEvent } from './types/analytics.types';
import { ConfigService } from '@/configuration/services/configService';

export class AnalyticsManager {
    private providers: AnalyticsProvider[] = [];
    private isEnabled: boolean = false;

    constructor() {
        try {
            const config = ConfigService.get();
            this.isEnabled = config.application.analytics?.enableTracking ?? false;
        } catch {
            console.warn('Config not available during analytics initialization, defaulting to disabled');
            this.isEnabled = false;
        }
    }

    addProvider(provider: AnalyticsProvider): void {
        if (!this.isEnabled) return;

        if (provider.isEnabled()) {
            this.providers.push(provider);
        }
    }

    removeProvider(providerName: string): void {
        this.providers = this.providers.filter((p) => p.name !== providerName);
    }

    trackPageView(event: PageViewEvent): void {
        if (!this.isEnabled) return;

        this.providers.forEach((provider) => {
            try {
                provider.trackPageView(event);
            } catch (error) {
                console.error(`Error tracking page view with ${provider.name}:`, error);
            }
        });
    }

    trackEvent(event: AnalyticsEvent): void {
        if (!this.isEnabled) return;

        this.providers.forEach((provider) => {
            try {
                provider.trackEvent(event);
            } catch (error) {
                console.error(`Error tracking event with ${provider.name}:`, error);
            }
        });
    }

    deleteCustomDimensions(ids: number[]): void {
        if (!this.isEnabled) return;

        this.providers.forEach((provider) => {
            try {
                provider.deleteCustomDimensions(ids);
            } catch (error) {
                console.error(`Error deleting custom dimensions with ${provider.name}:`, error);
            }
        });
    }

    setUserId(userId: string): void {
        if (!this.isEnabled) return;

        this.providers.forEach((provider) => {
            try {
                provider.setUserId(userId);
            } catch (error) {
                console.error(`Error setting user ID with ${provider.name}:`, error);
            }
        });
    }

    resetUserId(): void {
        if (!this.isEnabled) return;

        this.providers.forEach((provider) => {
            try {
                provider.resetUserId();
            } catch (error) {
                console.error(`Error resetting user ID with ${provider.name}:`, error);
            }
        });
    }

    enabled(): boolean {
        return this.isEnabled;
    }

    enable(): void {
        this.isEnabled = true;
    }

    disable(): void {
        this.isEnabled = false;
    }

    getProviders(): AnalyticsProvider[] {
        return [...this.providers];
    }
}
